import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, Share2, Eye } from "lucide-react";
import { api, photoSrc } from "@/lib/api";
import { BAR_NAME, INSTAGRAM_HANDLE } from "@/lib/config";

const SESSION_KEY = "miro_viewed_photos";
const getViewed = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]")); }
  catch { return new Set(); }
};
const markViewed = (id) => {
  const s = getViewed(); s.add(id);
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify([...s])); } catch { /* ignore */ }
};

export const CommunityGallery = ({ limit, refreshKey = 0 }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await api.publicPhotos();
      const list = limit ? data.slice(0, limit) : data;
      setPhotos(list);
      const viewed = getViewed();
      list.forEach((p) => {
        if (!viewed.has(p.id)) {
          markViewed(p.id);
          api.viewPhoto(p.id).catch(() => {});
        }
      });
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const share = async (photo, e) => {
    e.preventDefault();
    e.stopPropagation();
    const pageUrl = `${window.location.origin}/foto`;
    const imgUrl = photoSrc(photo.url);
    const text = `Guarda le foto di ${BAR_NAME} 🍸 Napoli · @${INSTAGRAM_HANDLE}`;
    api.sharePhoto(photo.id).catch(() => {});
    setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, shares: (p.shares || 0) + 1 } : p)));
    if (navigator.share) {
      try { await navigator.share({ title: BAR_NAME, text, url: pageUrl }); return; }
      catch { return; }
    }
    try { await navigator.clipboard.writeText(imgUrl); } catch { /* ignore */ }
    toast.success("Link copiato! Incollalo su Instagram. Apro WhatsApp per la condivisione.");
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${pageUrl}`)}`, "_blank");
  };

  if (!loading && photos.length === 0) {
    return (
      <div data-testid="community-empty" className="rounded-2xl ring-1 ring-dashed ring-white/15 p-12 text-center">
        <Camera className="w-10 h-10 text-muted-foreground/40 mx-auto mb-4" />
        <p className="text-muted-foreground">Ancora nessuna foto pubblicata. Sii il primo a condividere il tuo momento MI.RO'!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-testid="community-gallery">
      {photos.map((p, i) => (
        <motion.figure
          key={p.id}
          data-testid={`community-photo-${p.id}`}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: (i % 3) * 0.06 }}
          className="group relative overflow-hidden rounded-xl ring-1 ring-white/10 aspect-square"
        >
          <img src={photoSrc(p.url)} alt={p.caption || p.name} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

          <button
            type="button"
            onClick={(e) => share(p, e)}
            data-testid={`share-photo-${p.id}`}
            title="Condividi"
            className="absolute top-2 right-2 flex items-center justify-center w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm text-white ring-1 ring-white/20 opacity-0 group-hover:opacity-100 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
          >
            <Share2 className="w-4 h-4" />
          </button>

          <div className="absolute top-2 left-2 flex items-center gap-2">
            <span data-testid={`views-${p.id}`} className="flex items-center gap-1 text-xs text-white bg-black/45 backdrop-blur-sm rounded-full px-2 py-0.5 ring-1 ring-white/10">
              <Eye className="w-3 h-3" /> {p.views || 0}
            </span>
            {(p.shares || 0) > 0 && (
              <span data-testid={`shares-${p.id}`} className="flex items-center gap-1 text-xs text-primary bg-black/45 backdrop-blur-sm rounded-full px-2 py-0.5 ring-1 ring-primary/30">
                <Share2 className="w-3 h-3" /> {p.shares}
              </span>
            )}
          </div>

          <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {p.caption && <p className="text-sm text-white leading-tight">{p.caption}</p>}
            <p className="text-xs text-primary mt-0.5">
              {p.source === "owner" ? "MI.RO'" : `@ ${p.name}`}
            </p>
          </figcaption>
        </motion.figure>
      ))}
    </div>
  );
};
